// src/components/Login.tsx
import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { LegalAgreementVersion } from '../services/api.ts';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState<{
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone_number: string;
    role: 'customer' | 'trainer';
    fitness_goals: string;
  }>({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    role: 'customer',
    fitness_goals: ''
  });

  const [legalAgreements, setLegalAgreements] = useState({
    tos_accepted: false,
    privacy_accepted: false
  });

  const [currentVersions, setCurrentVersions] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();

  const handleLegalAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setLegalAgreements(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  useEffect(() => {
    fetchCurrentAgreements();
  }, []);

  const fetchCurrentAgreements = async () => {
    try {
      const response = await fetch('https://aztrainersbackend.onrender.com/api/legal/current-agreements/');
      const data = await response.json();

      const versions: Record<string, string> = {};
      data.agreements.forEach((agreement: LegalAgreementVersion) => {
        versions[agreement.agreement_type] = agreement.current_version;
      });
      setCurrentVersions(versions);
    } catch (error) {
      console.error('Failed to fetch current agreements:', error);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(credentials.email, credentials.password);
    } catch (err: any) {
      setError(err.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!legalAgreements.tos_accepted || !legalAgreements.privacy_accepted) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      setLoading(false);
      return;
    }

    try {
      await register(
        registerData.email,
        registerData.password,
        registerData.first_name,
        registerData.phone_number,
        registerData.username,
        legalAgreements.tos_accepted,
        legalAgreements.privacy_accepted,
        registerData.last_name,
        registerData.role,

      );
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setForgotPasswordMessage('');

    try {
      const response = await fetch('https://aztrainersbackend.onrender.com/api/forgot-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (data.success) {
        setForgotPasswordMessage(data.message);
        setForgotPasswordEmail('');
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (err) {
      setError('Failed to send reset email');
    }
    setLoading(false);
  };

 

  // Forgot Password Modal
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F4EE] via-[#F5EFE7] to-[#E6A756] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Reset Password</h1>
            <p className="text-gray-600">Enter your email to receive a reset link</p>
          </div>

          {error && (
            <div className="bg-[#F8F4EE] border border-[#D2824C] text-[#A0673D] p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {forgotPasswordMessage && (
            <div className="bg-[#B8C5A6] border border-[#9CAF88] text-[#5D4037] p-3 rounded-lg mb-4 text-sm">
              {forgotPasswordMessage}
            </div>
          )}

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <input
              type="email"
              placeholder="Email Address"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              required
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#E6A756] text-[#5D4037] p-3 rounded-lg hover:bg-[#D2824C] transition-colors disabled:opacity-50 font-medium"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForgotPassword(false);
                setForgotPasswordEmail('');
                setForgotPasswordMessage('');
                setError('');
              }}
              className="w-full bg-[#F8F4EE] text-[#5D4037] p-3 rounded-lg hover:bg-[#F5EFE7] transition-colors font-medium border border-[#D4C4B0]"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F4EE] via-[#F5EFE7] to-[#E6A756] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">AZTrainers</h1>
          <p className="text-gray-600">Connect. Train. Transform.</p>
        </div>

        

        <div className="border-t pt-6">
          <div className="flex mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-sm font-medium rounded-l-lg transition-colors ${isLogin
                ? 'bg-[#E6A756] text-[#5D4037]'
                : 'bg-[#F8F4EE] text-[#8D6E63] hover:bg-[#F5EFE7] border border-[#D4C4B0]'
                }`}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-sm font-medium rounded-r-lg transition-colors ${!isLogin
                ? 'bg-[#E6A756] text-[#5D4037]'
                : 'bg-[#F8F4EE] text-[#8D6E63] hover:bg-[#F5EFE7] border border-[#D4C4B0]'
                }`}
            >
              Register
            </button>
          </div>

          {error && (
            <div className="bg-[#F8F4EE] border border-[#D2824C] text-[#A0673D] p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {isLogin ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                placeholder="Email Address"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                required
              />

              <input
                type="password"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                required
              />
              {/* Forgot Password Link */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-[#8B5A3C] hover:text-[#5D4037] hover:underline"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E6A756] text-[#5D4037] p-3 rounded-lg hover:bg-[#D2824C] transition-colors disabled:opacity-50 font-medium"
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="First Name"
                  value={registerData.first_name}
                  onChange={(e) => setRegisterData({ ...registerData, first_name: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                  required
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={registerData.last_name}
                  onChange={(e) => setRegisterData({ ...registerData, last_name: e.target.value })}
                  className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Username"
                value={registerData.username}
                onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                required
              />

              <input
                type="tel"
                placeholder="Phone Number"
                value={registerData.phone_number}
                onChange={(e) => setRegisterData({ ...registerData, phone_number: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              />

              <input
                type="text"
                placeholder="Fitness Goals"
                value={registerData.fitness_goals}
                onChange={(e) => setRegisterData({ ...registerData, fitness_goals: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E6A756] focus:border-transparent"
              />

              {/* Legal Agreements Section - Customer Only */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">Legal Agreements</h4>

                {/* Terms of Service */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="tos_accepted"
                    name="tos_accepted"
                    checked={legalAgreements.tos_accepted}
                    onChange={handleLegalAgreementChange}
                    className="mt-1 h-4 w-4 text-[#E6A756] focus:ring-[#E6A756] border-gray-300 rounded"
                    required
                  />
                  <label htmlFor="tos_accepted" className="text-sm text-gray-700 flex-1">
                    I agree to the{' '}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      className="text-[#8B5A3C] hover:underline"
                    >
                      Terms of Service
                    </a>
                    {currentVersions.tos && (
                      <span className="text-gray-500 ml-1">(v{currentVersions.tos})</span>
                    )}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>

                {/* Privacy Policy */}
                <div className="flex items-start space-x-3">

                  <input
                    type="checkbox"
                    id="privacy_accepted"
                    name="privacy_accepted"
                    checked={legalAgreements.privacy_accepted}
                    onChange={handleLegalAgreementChange}
                    className="mt-1 h-4 w-4 text-[#E6A756] focus:ring-[#E6A756] border-gray-300 rounded"
                    required
                  />
                  <label htmlFor="privacy_accepted" className="text-sm text-gray-700 flex-1">
                    I agree to the{' '}

                    <a
                      href="/privacy-policy"
                      target="_blank"
                      className="text-[#8B5A3C] hover:underline"
                    >
                      Privacy Policy
                    </a>
                    {currentVersions.privacy && (
                      <span className="text-gray-500 ml-1">(v{currentVersions.privacy})</span>
                    )}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                </div>

                <p className="text-xs text-gray-500">
                  <span className="text-red-500">*</span> Required to create an account
                </p>

                <p className="text-xs text-gray-600 bg-[#F8F4EE] p-2 rounded border border-[#D4C4B0]">
                  💡 Looking to join as a trainer? Contact us directly - we personally onboard all trainers to ensure quality.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#E6A756] text-[#5D4037] p-3 rounded-lg hover:bg-[#D2824C] transition-colors disabled:opacity-50 font-medium"
              >
                {loading ? 'Creating Account...' : 'Register'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}